<template>
  <div v-if="loaded" class="rail-foot">
    <!-- AI-tokens mini-meter -->
    <div class="rail-foot__meter">
      <div class="rail-foot__meter-top">
        <span class="rail-foot__ai"><CygnusIcon name="bolt" /></span>
        <span class="rail-foot__meter-label">AI tokens</span>
        <span class="rail-foot__meter-num">
          <template v-if="ai.quota === null">{{ fmt(ai.used) }} <span class="cap">used</span></template>
          <template v-else>{{ fmt(ai.used) }}<span class="cap"> / {{ fmt(ai.quota) }}</span></template>
        </span>
      </div>
      <div v-if="ai.quota !== null" class="rail-foot__bar">
        <span :class="{ warn: pct >= 80 }" :style="{ width: pct + '%' }"></span>
      </div>
      <div class="rail-foot__meter-foot">
        <span>{{ leftText }}</span>
        <router-link to="/admin/usage-plans">Top up</router-link>
      </div>
    </div>

    <!-- Slim Settings affordance → the role-scoped Admin surface. F-RN-04:
         hidden for roles that can't reach /admin at all — the cheap, honest
         signal is whether the Admin leaf is even present in the already-
         loaded LHS menu (TheLeftSidebar passes it down); no new endpoint. -->
    <router-link v-if="showSettings" to="/admin" class="rail-foot__settings">
      <CygnusIcon name="gear" />
      <span>Settings</span>
      <span v-if="plan.name" class="rail-foot__plan" :title="plan.name">{{ planLabel }}</span>
    </router-link>
  </div>
</template>

<script>
/**
 * RailUsageMeter (icarus-theme-base) — the pinned rail foot cluster from the
 * LHS-rework (Screen A): an AI-tokens mini-meter (with the teal "this is AI
 * spend" identity + a top-up line) and a slim Settings affordance into the
 * role-scoped Admin surface, tagged with the current plan name.
 *
 * Reads `GET /api/v1/me/usage` (available to any authenticated user; no perm
 * arg). Renders nothing until loaded, and nothing if the fetch fails — the rail
 * degrades to just nav rather than showing an empty shell.
 */
import useApi from '@apexl/icarus-core/src/services/api';

export default {
  name: 'RailUsageMeter',
  props: {
    /** F-RN-04 — parent (TheLeftSidebar) passes this based on whether the
     * Admin entry is present in the already-loaded LHS menu. Defaults to
     * true so a consumer that doesn't wire the prop keeps today's
     * behaviour rather than silently losing the affordance. */
    showSettings: { type: Boolean, default: true },
  },
  data() {
    return {
      loaded: false,
      ai: { used: 0, quota: null, topup_available: 0, resets_at: 0 },
      plan: { name: '', can_manage: false },
    };
  },
  computed: {
    // F-RN-01 — "Internal (unlimited)" was clipping mid-word ("INTERNAL
    // (UNL…") inside the fixed-width foot chip. Drop a trailing parenthetical
    // qualifier for the on-rail label; the untruncated name is still exposed
    // via the chip's title attribute (see template).
    planLabel() {
      const n = this.plan.name || '';
      const shortened = n.replace(/\s*\([^)]*\)\s*$/, '').trim();
      return shortened || n;
    },
    pct() {
      if (this.ai.quota === null || this.ai.quota <= 0) return 0;
      return Math.min(100, Math.round((this.ai.used / this.ai.quota) * 100));
    },
    leftText() {
      if (this.ai.quota === null) {
        const t = this.ai.topup_available;
        return t > 0 ? `${this.fmt(t)} top-up` : 'Unlimited';
      }
      const left = Math.max(0, this.ai.quota - this.ai.used) + (this.ai.topup_available || 0);
      const resets = this.resetsText;
      return resets ? `${this.fmt(left)} left · ${resets}` : `${this.fmt(left)} left`;
    },
    resetsText() {
      if (!this.ai.resets_at) return '';
      try {
        const d = new Date(this.ai.resets_at * 1000);
        return 'resets ' + d.toLocaleDateString(undefined, { day: 'numeric', month: 'short' });
      } catch { return ''; }
    },
  },
  mounted() {
    this.load();
  },
  methods: {
    fmt(n) {
      return Number(n || 0).toLocaleString();
    },
    async load() {
      try {
        const res = await useApi().get('/api/v1/me/usage');
        const m = res?.meters?.ai_tokens;
        if (m) {
          this.ai = {
            used: m.used ?? 0,
            quota: m.quota ?? null,
            topup_available: m.topup_available ?? 0,
            resets_at: m.resets_at ?? res?.period?.resets_at ?? 0,
          };
        }
        if (res?.plan) {
          this.plan = { name: res.plan.name || '', can_manage: !!res.plan.can_manage };
        }
        this.loaded = true;
      } catch {
        // Silent — rail degrades to nav-only.
      }
    },
  },
};
</script>

<style lang="scss">
.rail-foot {
  flex: 0 0 auto;
  margin-top: 0.35rem;
  padding: 0.6rem 0.85rem 0.75rem;
  border-top: 1px solid var(--theme-rail-hairline);

  &__meter { padding: 0.15rem 0.1rem 0.4rem; }

  &__meter-top {
    display: flex;
    align-items: center;
    gap: 6px;
    font-size: 0.72rem;
    font-weight: 600;
    color: var(--theme-text-sidebar, rgba(255, 255, 255, 0.82));
  }

  &__ai {
    display: inline-flex;
    color: var(--theme-accent-secondary, #0EA5A4);
    i { font-size: 0.78rem; }
  }

  &__meter-num {
    margin-left: auto;
    font-family: var(--theme-font-mono, ui-monospace, monospace);
    font-size: 0.72rem;
    font-weight: 700;
    color: var(--theme-rail-text-strong);
    .cap { color: var(--theme-rail-text-muted); font-weight: 500; }
  }

  &__bar {
    height: 6px;
    border-radius: 999px;
    background: var(--theme-rail-track);
    overflow: hidden;
    margin-top: 6px;

    span {
      display: block;
      height: 100%;
      border-radius: 999px;
      background: var(--theme-accent-secondary, #14B8A6);
      transition: width 0.3s ease;
      &.warn { background: var(--theme-status-warning, #D97706); }
    }
  }

  &__meter-foot {
    display: flex;
    justify-content: space-between;
    align-items: center;
    font-size: 0.66rem;
    color: var(--theme-rail-text-muted);
    margin-top: 5px;

    a {
      color: var(--theme-accent-secondary, #14B8A6);
      font-weight: 600;
      text-decoration: none;
      &:hover { text-decoration: underline; }
    }
  }

  &__settings {
    display: flex;
    align-items: center;
    gap: 0.6rem;
    margin-top: 0.4rem;
    padding: 0.5rem 0.6rem;
    border-radius: 8px;
    color: var(--theme-text-sidebar, rgba(255, 255, 255, 0.82));
    text-decoration: none;
    font-size: 0.82rem;
    font-weight: 500;
    transition: background 0.12s, color 0.12s;

    i { width: 1.1rem; text-align: center; opacity: 0.85; flex: none; }

    // "Settings" label — fixed, never the thing that shrinks.
    > span:not(.rail-foot__plan) { flex: none; }

    &:hover { background: var(--theme-rail-hover-bg); color: var(--theme-rail-text-strong); }
    &.router-link-active { background: var(--theme-rail-active-bg); color: var(--theme-text-sidebar-active); }
  }

  &__plan {
    // Reads as a single-line chip regardless of plan-name length ("Internal
    // (unlimited)" was wrapping to 2 lines and growing to 35px tall — flex
    // items default to min-width:auto, which blocks shrinking/ellipsis
    // inside a flex row, so min-width:0 is required alongside the ellipsis
    // trio). Capped to leave room for the icon + "Settings" label before it
    // in the rail-foot__settings row.
    flex: 0 1 auto;
    min-width: 0;
    max-width: 108px;
    margin-left: auto;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    font-size: 0.6rem;
    line-height: 1.5;
    font-weight: 800;
    letter-spacing: 0.04em;
    text-transform: uppercase;
    color: var(--theme-text-sidebar-active);
    background: var(--theme-rail-active-bg);
    border: 1px solid transparent;
    border-radius: 999px;
    padding: 3px 8px;
  }
}
</style>
